import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Coordinator/photo_editor view of high-res photos uploaded to a single
// spot. Shows the photographer name + size; click opens the original.
export default function SpotPhotosGallery({ eventId, spotId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/events/${eventId}/spots/${spotId}/photos`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setPhotos(r.data || []);
    } catch {
      /* swallow */
    } finally {
      setLoading(false);
    }
  }, [eventId, spotId]);

  useEffect(() => { refresh(); }, [refresh]);

  const fmtBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(
        `${API}/spot-photos/${id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove');
    }
  };

  if (loading && photos.length === 0) return null;
  return (
    <div className="border-t pt-4">
      <label className="block text-sm font-medium mb-2">📸 Photos from photographers ({photos.length})</label>
      {photos.length === 0 ? (
        <p className="text-xs text-slate-500">
          No photos uploaded yet. Photographers can attach high-res originals through the share link.
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p) => {
            const fullUrl = p.url.startsWith('http') ? p.url : `${BACKEND_URL}${p.url}`;
            return (
              <div key={p.id} className="relative group border rounded overflow-hidden bg-slate-50">
                <a href={fullUrl} target="_blank" rel="noreferrer" download={p.filename || true}>
                  <img src={fullUrl} alt="" className="w-full h-24 object-cover" />
                </a>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 flex items-center justify-between">
                  <span className="truncate">{p.photographer_name || '—'}</span>
                  <span>{fmtBytes(p.size_bytes)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500 hover:bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
